import { Component } from '@theme/component';

const DESKTOP_QUERY = '(min-width: 750px)';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const VISIBLE_RATIO = 0.5;

/**
 * @typedef {Object} ThinBannerVideoRefs
 * @property {HTMLButtonElement} [toggle] - Play/pause button
 */

/**
 * Plays the thin banner's video only while at least half of it is on screen,
 * with a play/pause button that always reflects the state (WCAG 2.2.2).
 * A user pause sticks until the user presses play again. With reduced motion,
 * or when the browser blocks autoplay, the video starts paused.
 *
 * When separate desktop and mobile videos exist, only the one for the current
 * breakpoint plays.
 *
 * @extends {Component<ThinBannerVideoRefs>}
 */
class ThinBannerVideo extends Component {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  #desktopQuery = window.matchMedia(DESKTOP_QUERY);
  #isInView = false;
  #isPausedByUser = false;

  connectedCallback() {
    super.connectedCallback();

    const reducedMotion = window.matchMedia(REDUCED_MOTION_QUERY).matches;
    this.#isPausedByUser = this.dataset.autoplay !== 'true' || reducedMotion;

    for (const video of this.#videos) {
      video.muted = true;
      video.addEventListener('ended', this.#handleEnded);
    }

    this.#desktopQuery.addEventListener('change', this.#update);
    this.#observer = new IntersectionObserver(this.#handleIntersection, { threshold: [0, VISIBLE_RATIO] });
    this.#observer.observe(this);
    this.#syncToggle();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#observer?.disconnect();
    this.#desktopQuery.removeEventListener('change', this.#update);

    for (const video of this.#videos) {
      video.removeEventListener('ended', this.#handleEnded);
      video.pause();
    }
  }

  /**
   * Handles the play/pause button.
   */
  togglePlayback() {
    this.#isPausedByUser = !this.#isPausedByUser;

    // The user can see the button they just pressed, so play now even if less than half is on screen.
    if (!this.#isPausedByUser) this.#isInView = true;

    this.#update();
  }

  /** @returns {HTMLVideoElement[]} */
  get #videos() {
    return Array.from(this.querySelectorAll('video'));
  }

  /** @returns {HTMLVideoElement | undefined} */
  get #activeVideo() {
    const videos = this.#videos;

    if (videos.length < 2) return videos[0];

    const viewport = this.#desktopQuery.matches ? 'desktop' : 'mobile';

    return videos.find((video) => video.closest(`[data-viewport="${viewport}"]`)) ?? videos[0];
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #handleIntersection = (entries) => {
    for (const entry of entries) {
      this.#isInView = entry.isIntersecting && entry.intersectionRatio >= VISIBLE_RATIO;
    }

    this.#update();
  };

  #handleEnded = () => {
    this.#isPausedByUser = true;
    this.#syncToggle();
  };

  #update = () => {
    const activeVideo = this.#activeVideo;

    for (const video of this.#videos) {
      if (video !== activeVideo) video.pause();
    }

    if (activeVideo && this.#isInView && !this.#isPausedByUser) {
      this.#play(activeVideo);
    } else {
      activeVideo?.pause();
    }

    this.#syncToggle();
  };

  /** @param {HTMLVideoElement} video */
  async #play(video) {
    try {
      await video.play();
    } catch (error) {
      // Autoplay blocked (for example iOS Low Power Mode): show the play button.
      // AbortError only means a pause() interrupted the play() call, so it's ignored.
      if (error instanceof DOMException && error.name === 'NotAllowedError') {
        this.#isPausedByUser = true;
        this.#syncToggle();
      }
    }
  }

  #syncToggle() {
    const { toggle } = this.refs;

    if (!toggle) return;

    const isPlaying = !this.#isPausedByUser;
    const label = isPlaying ? this.dataset.pauseLabel : this.dataset.playLabel;

    toggle.dataset.state = isPlaying ? 'playing' : 'paused';
    if (label) toggle.setAttribute('aria-label', label);
  }
}

if (!customElements.get('thin-banner-video')) {
  customElements.define('thin-banner-video', ThinBannerVideo);
}
